import {
  Box,
  Button,
  Collapse,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { ILegalTransactionRelationshipRealEstate } from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { IRealEstatePurchaseContract } from '../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/IRealEstatePurchaseContract';
import { IPurchaseContractRelationType } from '../../../../Interfaces/IPurchaseContractRelationType';

interface IProps {
  formValues: ILegalTransactionRelationshipRealEstate;
  purchaseContract: IRealEstatePurchaseContract;
  purchaseContractRelationTypes: IPurchaseContractRelationType[];
  handleNext: () => void;
  handleBack: () => void;
  setFormValues: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationshipRealEstate>
  >;
  setPurchaseContract: React.Dispatch<
    React.SetStateAction<IRealEstatePurchaseContract>
  >;
}

export const CreateRealEstatePartialArea: React.FC<IProps> = ({
  handleNext,
  handleBack,
  setFormValues,
  setPurchaseContract,
  purchaseContractRelationTypes,
  formValues: { hasPartialArea, isMeasured, hasEasement, Area, EasementText },
  purchaseContract: {
    PaymentMeasurement_idPurchaseContractRelationType,
    OrderMeasurement_idPurchaseContractRelationType,
  },
}) => {
  const [isPartialAreaSelectionMade, setIsPartialAreaSelectionMade] =
    useState(false);
  const [isMeasuredSelectionMade, setIsMeasuredSelectionMade] = useState(false);
  const [isEasementSelectionMade, setIsEasementSelectionMade] = useState(false);

  const isValid = useMemo(() => {
    return (
      isPartialAreaSelectionMade &&
      (!hasPartialArea ||
        (isMeasuredSelectionMade &&
          PaymentMeasurement_idPurchaseContractRelationType &&
          isEasementSelectionMade &&
          ((!isMeasured && OrderMeasurement_idPurchaseContractRelationType) ||
            (isMeasured && Area)) &&
          (!hasEasement || (hasEasement && EasementText))))
    );
  }, [
    isPartialAreaSelectionMade,
    hasPartialArea,
    isMeasuredSelectionMade,
    PaymentMeasurement_idPurchaseContractRelationType,
    isEasementSelectionMade,
    isMeasured,
    OrderMeasurement_idPurchaseContractRelationType,
    Area,
    hasEasement,
    EasementText,
  ]);

  return (
    <Box>
      <Box
        display='flex'
        height={80}
        alignItems='center'
        justifyContent='center'
      >
        Wird das ganze Gründstück verkauft oder eine Teilfläche von diesem?
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Button
          variant={
            !isPartialAreaSelectionMade || hasPartialArea
              ? 'outlined'
              : 'contained'
          }
          onClick={() => {
            setIsPartialAreaSelectionMade(true);
            setIsEasementSelectionMade(false);
            setIsMeasuredSelectionMade(false);
            setFormValues(x => ({
              ...x,
              hasPartialArea: false,
              isMeasured: false,
              hasEasement: false,
              Area: null,
              EasementText: null,
            }));
            setPurchaseContract(x => ({
              ...x,
              PaymentMeasurement_idPurchaseContractRelationType: null,
              OrderMeasurement_idPurchaseContractRelationType: null,
            }));
          }}
          sx={{ mr: 1 }}
        >
          Ganzes Grundstück
        </Button>

        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          variant={
            !isPartialAreaSelectionMade || !hasPartialArea
              ? 'outlined'
              : 'contained'
          }
          onClick={() => {
            setIsPartialAreaSelectionMade(true);
            setFormValues(x => ({ ...x, hasPartialArea: true }));
          }}
        >
          Teilbereich
        </Button>
      </Box>

      <Collapse in={isPartialAreaSelectionMade && hasPartialArea}>
        <>
          <Box
            display='flex'
            height={80}
            alignItems='center'
            justifyContent='center'
          >
            Wurde das Grundstück vermessen?
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              sx={{ mr: 1 }}
              variant={
                !isMeasuredSelectionMade || isMeasured
                  ? 'outlined'
                  : 'contained'
              }
              onClick={() => {
                setIsMeasuredSelectionMade(true);
                setIsEasementSelectionMade(false);
                setFormValues(x => ({
                  ...x,
                  isMeasured: false,
                  hasEasement: false,
                  EasementText: null,
                  Area: null,
                }));
                setPurchaseContract(x => ({
                  ...x,
                  OrderMeasurement_idPurchaseContractRelationType:
                    purchaseContractRelationTypes[0]
                      .idPurchaseContractRelationType,
                  PaymentMeasurement_idPurchaseContractRelationType:
                    purchaseContractRelationTypes[0]
                      .idPurchaseContractRelationType,
                }));
              }}
            >
              Nein
            </Button>

            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              variant={
                !isMeasuredSelectionMade || !isMeasured
                  ? 'outlined'
                  : 'contained'
              }
              onClick={() => {
                setIsMeasuredSelectionMade(true);
                setIsEasementSelectionMade(false);
                setFormValues(x => ({
                  ...x,
                  isMeasured: true,
                  hasEasement: false,
                  EasementText: null,
                }));
                setPurchaseContract(x => ({
                  ...x,
                  OrderMeasurement_idPurchaseContractRelationType: null,
                  PaymentMeasurement_idPurchaseContractRelationType:
                    purchaseContractRelationTypes[0]
                      .idPurchaseContractRelationType,
                }));
              }}
            >
              Ja
            </Button>
          </Box>
        </>
      </Collapse>

      <Collapse
        in={
          isPartialAreaSelectionMade &&
          hasPartialArea &&
          isMeasuredSelectionMade &&
          isMeasured
        }
      >
        <Typography sx={{ mt: 5, mb: 1 }}>
          Wie groß ist die Fläche des Teilbereichs?
        </Typography>
        <NumericFormat
          value={Area ?? ''}
          suffix=' qm'
          fixedDecimalScale
          thousandsGroupStyle='thousand'
          thousandSeparator='.'
          decimalSeparator=','
          fullWidth
          customInput={TextField}
          onValueChange={values => {
            setFormValues(x => ({
              ...x,
              Area: values.floatValue ?? null,
            }));
          }}
          allowNegative={false}
          label='Fläche'
          size='small'
          required
        />
      </Collapse>
      <Collapse
        in={
          isPartialAreaSelectionMade &&
          hasPartialArea &&
          isMeasuredSelectionMade &&
          !isMeasured
        }
      >
        <Typography sx={{ mt: 5 }}>Wer beauftragt die Vermessung?</Typography>
        <TextField
          sx={{ mt: 1 }}
          label='Auftraggeber/inn'
          value={
            OrderMeasurement_idPurchaseContractRelationType ??
            purchaseContractRelationTypes[0].idPurchaseContractRelationType
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setPurchaseContract(x => ({
              ...x,
              OrderMeasurement_idPurchaseContractRelationType: Number(
                event.target.value
              ),
            }))
          }
          size='small'
          fullWidth
          select
          required
        >
          {purchaseContractRelationTypes.map((x, i) => (
            <MenuItem key={i} value={x.idPurchaseContractRelationType}>
              {x.PurchaseContractRelationType}
            </MenuItem>
          ))}
        </TextField>
      </Collapse>
      <Collapse
        in={
          isPartialAreaSelectionMade &&
          hasPartialArea &&
          isMeasuredSelectionMade
        }
      >
        <Typography sx={{ mt: 3 }}>
          Wer übernimmt die Kosten für die Vermessung?
        </Typography>
        <TextField
          sx={{ mt: 1 }}
          label='Zahlungspflichtiger'
          value={
            PaymentMeasurement_idPurchaseContractRelationType ??
            purchaseContractRelationTypes[0].idPurchaseContractRelationType
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setPurchaseContract(x => ({
              ...x,
              PaymentMeasurement_idPurchaseContractRelationType: Number(
                event.target.value
              ),
            }))
          }
          size='small'
          fullWidth
          select
          required
        >
          {purchaseContractRelationTypes.map((x, i) => (
            <MenuItem key={i} value={x.idPurchaseContractRelationType}>
              {x.PurchaseContractRelationType}
            </MenuItem>
          ))}
        </TextField>
      </Collapse>
      <Collapse
        in={
          isPartialAreaSelectionMade &&
          hasPartialArea &&
          isMeasuredSelectionMade
        }
      >
        <>
          <Box
            display='flex'
            height={80}
            alignItems='center'
            justifyContent='center'
          >
            Werden Dienstbarkeiten (Wegerecht, Leitungsrecht, Ausblicksrecht,
            Nießbrauch etc.) eingeräumt?
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              variant={
                !isEasementSelectionMade || hasEasement
                  ? 'outlined'
                  : 'contained'
              }
              onClick={() => {
                setIsEasementSelectionMade(true);
                setFormValues(x => ({
                  ...x,
                  hasEasement: false,
                  EasementText: null,
                }));
              }}
              sx={{ mr: 1 }}
            >
              Nein
            </Button>

            <Box sx={{ flex: '1 1 auto' }} />
            <Button
              variant={
                !isEasementSelectionMade || !hasEasement
                  ? 'outlined'
                  : 'contained'
              }
              onClick={() => {
                setIsEasementSelectionMade(true);
                setFormValues(x => ({ ...x, hasEasement: true }));
              }}
            >
              Ja
            </Button>
          </Box>
        </>
      </Collapse>

      <Collapse
        in={
          isPartialAreaSelectionMade &&
          hasPartialArea &&
          isMeasuredSelectionMade &&
          isEasementSelectionMade &&
          hasEasement
        }
      >
        <Typography sx={{ mt: 5 }}>
          Bitte erfassen Sie alle Dienstbarkeiten
        </Typography>
        <TextField
          sx={{ mt: 1 }}
          label='Dienstbarkeiten'
          value={EasementText ?? ''}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFormValues(x => ({
              ...x,
              EasementText: event.target.value,
            }))
          }
          size='small'
          fullWidth
          multiline
          rows={3}
        />
      </Collapse>

      <Box sx={{ mt: 5 }}>
        <Button variant='outlined' onClick={() => handleBack()}>
          Zurück
        </Button>

        <Button
          onClick={() => handleNext()}
          variant='contained'
          sx={{ float: 'right' }}
          disabled={!isValid}
        >
          Weiter
        </Button>
      </Box>
    </Box>
  );
};
